import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  MenuItem,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function normalizeWfmcForDisplay(wfmc) {
  const raw = String(wfmc || '').trim();
  if (!raw) return '';
  const mac = raw.replace(/[^0-9a-fA-F]/g, '').toUpperCase();
  if (mac.length === 12) return mac;
  return raw.toUpperCase();
}

function isPrivateIP(ip) {
  if (!ip) return false;
  const s = String(ip);
  return /^10\./.test(s) || /^192\.168\./.test(s) || /^172\.(1[6-9]|2\d|3[01])\./.test(s);
}

function parseVersionFromFileName(fileName) {
  const m = String(fileName || '').match(/\bv\.?([0-9]+(?:\.[0-9]+)+)\b/i);
  return m ? m[1] : '';
}

function makeAuthHeaderFromCreds(creds) {
  if (!creds?.user || !creds?.pass) return {};
  return {
    Authorization: `Basic ${btoa(`${creds.user}:${creds.pass}`)}`,
  };
}

export default function FleetManagementPage() {
  const [inventory, setInventory] = useState([]); // {fileName, version, size, mtimeMs}
  const [policy, setPolicy] = useState(null); // {defaultFile, overrides}
  const [recentFiles, setRecentFiles] = useState([]); // {name,size,sizeHuman,mtime}
  const [devicesWfmc, setDevicesWfmc] = useState([]);
  const [downloadLog, setDownloadLog] = useState([]); // recent OTA downloads

  const [selectedDefaultFile, setSelectedDefaultFile] = useState('');
  const [wfmcInput, setWfmcInput] = useState('');
  const [selectedOverrideFile, setSelectedOverrideFile] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  // Stored only in-memory per page session.
  const [adminCreds, setAdminCreds] = useState(null);

  const fileOptions = useMemo(() => {
    return (inventory || []).map((x) => ({
      ...x,
      label: `${x.fileName}${x.version ? ` (v${x.version})` : ''}`,
    }));
  }, [inventory]);

  const normalizedWfmc = useMemo(() => normalizeWfmcForDisplay(wfmcInput), [wfmcInput]);

  const currentOverrideFile = useMemo(() => {
    if (!policy?.overrides || !normalizedWfmc) return '';
    return policy.overrides[normalizedWfmc] || '';
  }, [policy, normalizedWfmc]);

  const effectiveFile = useMemo(() => {
    return currentOverrideFile || policy?.defaultFile || '';
  }, [currentOverrideFile, policy]);

  const effectiveVersion = useMemo(() => parseVersionFromFileName(effectiveFile), [effectiveFile]);

  const loadInventory = useCallback(async () => {
    const r = await fetch('/api/admin/firmware/inventory');
    const j = await r.json();
    if (!j?.ok) throw new Error(j?.message || 'Failed to load inventory');
    setInventory(Array.isArray(j.items) ? j.items : []);
  }, []);

  const loadPolicy = useCallback(async () => {
    const r = await fetch('/api/admin/firmware/policy');
    const j = await r.json();
    if (!j?.ok) throw new Error(j?.message || 'Failed to load policy');
    setPolicy(j.policy || null);
    // Keep selectors aligned
    if (j.policy?.defaultFile) setSelectedDefaultFile(j.policy.defaultFile);
  }, []);

  const loadRecentFiles = useCallback(async () => {
    const r = await fetch('/api/firmware/list');
    const j = await r.json();
    if (!j?.ok) throw new Error(j?.error || 'Failed to load recent firmware files');
    setRecentFiles(Array.isArray(j.files) ? j.files : []);
  }, []);

  const loadDownloadLog = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/firmware/downloads?limit=100');
      const j = await r.json();
      setDownloadLog(Array.isArray(j.downloads) ? j.downloads : []);
    } catch {
      setDownloadLog([]);
    }
  }, []);

  const loadDevices = useCallback(async () => {
    try {
      const r = await fetch('/devices?action=all');
      const j = await r.json();

      const arr = Array.isArray(j) ? j : Array.isArray(j?.devices) ? j.devices : [];
      const wfmcList = arr
        .map((d) => d?.wfmc || d?.WFMC || d?.mac || d?.MAC || d?.id)
        .filter(Boolean)
        .map((v) => normalizeWfmcForDisplay(v));
      // unique
      const uniq = Array.from(new Set(wfmcList.filter(Boolean)));
      uniq.sort();
      setDevicesWfmc(uniq);
    } catch {
      // Devices endpoint depends on backend availability; keep UI usable even if it fails.
      setDevicesWfmc([]);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setMessage('');
    await Promise.allSettled([loadInventory(), loadPolicy(), loadRecentFiles(), loadDevices(), loadDownloadLog()]);
  }, [loadInventory, loadPolicy, loadRecentFiles, loadDevices, loadDownloadLog]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const ensureCreds = useCallback(() => {
    if (adminCreds?.user && adminCreds?.pass) return adminCreds;
    const user = window.prompt('Admin username:', 'admin');
    if (user == null) return null;
    const pass = window.prompt('Admin password:', 'admin');
    if (pass == null) return null;
    const next = { user, pass };
    setAdminCreds(next);
    return next;
  }, [adminCreds]);

  const postWithAuth = useCallback(
    async (url, body) => {
      const creds = ensureCreds();
      if (!creds) throw new Error('Cancelled');
      const r = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...makeAuthHeaderFromCreds(creds),
        },
        body: JSON.stringify(body || {}),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) {
        if (r.status === 401) {
          // force re-prompt next time
          setAdminCreds(null);
          throw new Error('Unauthorized (wrong admin credentials)');
        }
        throw new Error(j?.message || j?.error || `Request failed (${r.status})`);
      }
      return j;
    },
    [ensureCreds]
  );

  const onSetDefault = useCallback(async () => {
    try {
      setBusy(true);
      setMessage('');
      if (!selectedDefaultFile) throw new Error('Select a default firmware file');
      await postWithAuth('/api/admin/firmware/default', { fileName: selectedDefaultFile });
      await loadPolicy();
      setMessage('Default firmware updated');
    } catch (e) {
      setMessage(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [selectedDefaultFile, postWithAuth, loadPolicy]);

  const onSetOverride = useCallback(async () => {
    try {
      setBusy(true);
      setMessage('');
      if (!normalizedWfmc) throw new Error('Enter a WFMC');
      if (!selectedOverrideFile) throw new Error('Select a firmware file');
      await postWithAuth('/api/admin/firmware/override', { wfmc: normalizedWfmc, fileName: selectedOverrideFile });
      await loadPolicy();
      setMessage(`Override set for ${normalizedWfmc}`);
    } catch (e) {
      setMessage(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [normalizedWfmc, selectedOverrideFile, postWithAuth, loadPolicy]);

  const onClearOverride = useCallback(async () => {
    try {
      setBusy(true);
      setMessage('');
      if (!normalizedWfmc) throw new Error('Enter a WFMC');
      await postWithAuth('/api/admin/firmware/override/clear', { wfmc: normalizedWfmc });
      await loadPolicy();
      setMessage(`Override cleared for ${normalizedWfmc}`);
    } catch (e) {
      setMessage(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [normalizedWfmc, postWithAuth, loadPolicy]);

  const onUploadFirmware = useCallback(async (file) => {
    if (!file) return;
    try {
      setBusy(true);
      setMessage('');
      const form = new FormData();
      form.append('firmware', file);
      const r = await fetch('/api/firmware/upload', { method: 'POST', body: form });
      const j = await r.json();
      if (!r.ok || !j?.ok) throw new Error(j?.error || j?.message || 'Upload failed');
      setMessage(`Uploaded: ${j.savedAs || file.name}`);
      await Promise.allSettled([loadRecentFiles(), loadInventory()]);
    } catch (e) {
      setMessage(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [loadRecentFiles, loadInventory]);

  return (
    <>
      <Helmet>
        <title>Fleet Management | Terrra Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }} spacing={2}>
          <Typography variant="h4">Fleet Management</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={refreshAll} disabled={busy}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="body2">
                  Default file: <b>{effectiveFile || '-'}</b>
                </Typography>
                <Typography variant="body2">
                  Default version: <b>{effectiveVersion || '-'}</b>
                </Typography>

                {message ? (
                  <Typography variant="body2" sx={{ mt: 2 }} color={message.toLowerCase().includes('error') || message.toLowerCase().includes('fail') || message.toLowerCase().includes('unauthorized') ? 'error' : 'text.primary'}>
                    {message}
                  </Typography>
                ) : null}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="h6">Firmware Control</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">Upload new .bin</Typography>
                    <Button variant="contained" component="label" size="small" disabled={busy}>
                      Upload
                      <input
                        type="file"
                        accept=".bin"
                        hidden
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          e.target.value = '';
                          onUploadFirmware(f);
                        }}
                      />
                    </Button>
                    <Button size="small" variant="outlined" onClick={loadRecentFiles} disabled={busy}>
                      Refresh
                    </Button>
                  </Stack>
                </Stack>
                <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
                  {recentFiles.length ? (
                    recentFiles.map((f) => (
                      <Box key={f.name} sx={{ display: 'flex', gap: 1, alignItems: 'center', borderBottom: '1px solid rgba(145,158,171,0.16)', py: 0.5 }}>
                        <Radio
                          size="small"
                          checked={selectedDefaultFile === f.name}
                          onChange={() => setSelectedDefaultFile(f.name)}
                          sx={{ p: 0.5 }}
                        />
                        <Typography variant="caption" sx={{ flexGrow: 1, fontWeight: policy?.defaultFile === f.name ? 600 : 400 }}>
                          {f.name}
                          {policy?.defaultFile === f.name ? <span style={{ marginLeft: 6, opacity: 0.5 }}>(default)</span> : null}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
                          {parseVersionFromFileName(f.name) ? `v${parseVersionFromFileName(f.name)}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 55 }}>
                          {f.sizeHuman || formatBytes(f.size)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                          {new Date(f.mtime).toLocaleString()}
                        </Typography>
                        {selectedDefaultFile === f.name && selectedDefaultFile !== policy?.defaultFile && (
                          <Button size="small" variant="outlined" onClick={onSetDefault} disabled={busy}>
                            Set as default
                          </Button>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary">No firmware files found.</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography variant="h6">Per-device override</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" size="small" onClick={onSetOverride} disabled={busy || !normalizedWfmc || !selectedOverrideFile}>
                      Set override
                    </Button>
                    <Button variant="outlined" color="error" size="small" onClick={onClearOverride} disabled={busy || !normalizedWfmc}>
                      Clear
                    </Button>
                  </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                  <Autocomplete
                    freeSolo
                    options={devicesWfmc}
                    value={wfmcInput}
                    onInputChange={(e, v) => setWfmcInput(v)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="WFMC (MAC)" placeholder="AA:BB:CC:DD:EE:FF" />
                    )}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Firmware file"
                    value={selectedOverrideFile}
                    onChange={(e) => setSelectedOverrideFile(e.target.value)}
                    sx={{ flex: 1 }}
                  >
                    {fileOptions.map((o) => (
                      <MenuItem key={o.fileName} value={o.fileName}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  Current override for <b>{normalizedWfmc || '-'}</b>: <b>{currentOverrideFile || '(using default)'}</b>
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="h6">OTA activity</Typography>
                  <Button size="small" variant="outlined" onClick={loadDownloadLog} disabled={busy}>Refresh</Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Devices that downloaded a firmware binary from this server.
                </Typography>
                <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                  {downloadLog.length ? (
                    downloadLog.map((d, i) => {
                      const sameNet = isPrivateIP(d.local_ip);
                      return (
                        <Box key={i} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid rgba(145,158,171,0.16)', py: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 130 }}>{d.wfmc}</Typography>
                          {d.org && (
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 45 }}>{d.org}</Typography>
                          )}
                          <Typography variant="caption" sx={{ minWidth: 80 }}>
                            {d.prev_version ? `v${d.prev_version} → v${d.version}` : `v${d.version}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, minWidth: 120 }}>{d.file_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{new Date(d.downloaded_at).toLocaleString()}</Typography>
                          {sameNet && (
                            <Button size="small" variant="outlined" href={`http://${d.local_ip}/`} target="_blank" rel="noopener noreferrer">
                              Open
                            </Button>
                          )}
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="caption" color="text.secondary">No downloads recorded yet.</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
